import React, { useState } from 'react';
import { TestCatalogItem } from '@/hooks/use-test-catalog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export interface TestSelectionProps {
  availableTests: TestCatalogItem[];
  selectedTestIds: Set<string>;
  onAddTest: (test: TestCatalogItem) => void;
}

const TestSelection: React.FC<TestSelectionProps> = ({ 
  availableTests, 
  selectedTestIds, 
  onAddTest 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  // Extract unique categories
  const categories = ['all', ...new Set(availableTests.map(test => test.category || 'other'))];

  // Filter tests by search term and category
  const filteredTests = availableTests.filter(test => {
    const matchesSearch = searchTerm === '' || 
      test.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (test.description && test.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = activeCategory === 'all' || test.category === activeCategory;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Select Tests</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search tests..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Categories */}
        <Tabs defaultValue="all" value={activeCategory} onValueChange={setActiveCategory}>
          <TabsList className="w-full overflow-x-auto flex-nowrap flex justify-start">
            {categories.map(category => (
              <TabsTrigger key={category} value={category} className="whitespace-nowrap">
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={activeCategory} className="mt-4">
            {filteredTests.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No tests found matching your search.</p>
            ) : (
              <ul className="space-y-2">
                {filteredTests.map(test => (
                  <li key={test.id} className="relative">
                    <div className="flex justify-between items-center p-3 border rounded-md hover:bg-muted/40 transition-colors">
                      <div>
                        <h3 className="font-medium">{test.name}</h3>
                        {test.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">{test.description}</p>
                        )}
                        {test.category && (
                          <Badge variant="outline" className="mt-1">{test.category}</Badge>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => onAddTest(test)}
                        disabled={selectedTestIds.has(test.id)}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        {selectedTestIds.has(test.id) ? 'Added' : 'Add'}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default TestSelection; 